import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import { api } from '../lib/api'
import { useStore } from '../lib/store'

/**
 * StationLoginPage — credential-free login for a shared/public computer.
 * Shows a QR + short code; the clinician approves from their phone (already
 * logged in). No password is ever typed on this machine.
 */
export function StationLoginPage() {
  const navigate = useNavigate()
  const setAuth = useStore((s: any) => s.setAuthFromToken)
  const [userCode, setUserCode] = useState('')
  const [verifyUrl, setVerifyUrl] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [status, setStatus] = useState<'starting' | 'waiting' | 'approved' | 'expired' | 'denied' | 'error'>('starting')
  const [error, setError] = useState('')
  const pollTimer = useRef<any>(null)
  const expireTimer = useRef<any>(null)

  async function begin() {
    setStatus('starting'); setError('')
    try {
      const label = navigator.userAgent.includes('Win') ? 'Workstation' : 'Shared computer'
      const res = await api.deviceStart(label)
      setUserCode(res.user_code)
      setVerifyUrl(res.verification_uri)
      try { setQrDataUrl(await QRCode.toDataURL(res.verification_uri_complete, { width: 240, margin: 1 })) } catch { /* QR optional */ }
      setStatus('waiting')

      // Poll
      const poll = async () => {
        try {
          const p = await api.devicePoll(res.device_code)
          if (p.status === 'approved' && p.access_token) {
            clearInterval(pollTimer.current); clearTimeout(expireTimer.current)
            api.setToken(p.access_token)
            if (setAuth) setAuth(p.access_token, p.doctor)
            setStatus('approved')
            setTimeout(() => navigate('/'), 600)
          } else if (p.status === 'denied') {
            clearInterval(pollTimer.current); setStatus('denied')
          } else if (p.status === 'expired' || p.status === 'claimed') {
            clearInterval(pollTimer.current); setStatus('expired')
          }
        } catch { /* transient; keep polling */ }
      }
      pollTimer.current = setInterval(poll, (res.interval || 3) * 1000)
      expireTimer.current = setTimeout(() => { clearInterval(pollTimer.current); setStatus('expired') }, (res.expires_in || 300) * 1000)
    } catch (e: any) {
      setError(e?.message || 'Could not start login'); setStatus('error')
    }
  }

  useEffect(() => {
    begin()
    return () => { clearInterval(pollTimer.current); clearTimeout(expireTimer.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 }}>
      <div style={{ width: 420, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,.08)', padding: 32, textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: '#0f172a' }}>Sign in with your phone</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
          No password needed on this computer. Scan the code with your phone (where you're already signed in) and approve.
        </p>

        {status === 'starting' && <p style={{ color: '#64748b' }}>Preparing secure code…</p>}

        {(status === 'waiting' || status === 'approved') && (
          <>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="Login QR code" style={{ width: 240, height: 240, margin: '0 auto', display: 'block', borderRadius: 12 }} />
              : <div style={{ width: 240, height: 240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>QR unavailable — use the code below</div>}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Or enter this code at</div>
              <div style={{ fontSize: 13, color: '#334155', marginBottom: 8 }}>{verifyUrl}</div>
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 4, color: '#0f172a', fontFamily: 'monospace' }}>{userCode}</div>
            </div>
            <p style={{ marginTop: 18, fontSize: 13, color: status === 'approved' ? '#16a34a' : '#64748b' }}>
              {status === 'approved' ? '✓ Approved — signing you in…' : 'Waiting for approval on your phone…'}
            </p>
          </>
        )}

        {status === 'expired' && (
          <div>
            <p style={{ color: '#b45309', marginBottom: 16 }}>This code expired for your security.</p>
            <button onClick={begin} style={btn}>Generate a new code</button>
          </div>
        )}
        {status === 'denied' && (
          <div>
            <p style={{ color: '#dc2626', marginBottom: 16 }}>Login was declined on the phone.</p>
            <button onClick={begin} style={btn}>Try again</button>
          </div>
        )}
        {status === 'error' && (
          <div>
            <p style={{ color: '#dc2626', marginBottom: 16 }}>{error}</p>
            <button onClick={begin} style={btn}>Retry</button>
          </div>
        )}

        <div style={{ marginTop: 28, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
          <a href="/login" style={{ color: '#2563eb', fontSize: 13, textDecoration: 'none' }}>Use email &amp; password instead</a>
        </div>
      </div>
    </div>
  )
}

const btn: React.CSSProperties = {
  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
}
